// ─────────────────────────────────────────────────────────────────────────────
// Services
// ─────────────────────────────────────────────────────────────────────────────
// The nine trades Five Lights Reno actually offers. Nothing has been added to
// round the list out, and nothing claimed here goes beyond what the business
// already says about itself.
//
// SLUGS ARE FROZEN. `/services/kitchen`, `/services/legal-basement` and the
// rest are already in the live sitemap and may be indexed, so the URL keeps its
// original slug even where the display name has been rewritten (slug `bath`
// now presents as "Bathroom Renovation"). Renaming a slug means adding a
// redirect in App.js — don't do it casually.
//
// `photo` is a key into data/media.js, or null where no genuine photo of that
// service exists yet (kitchen). Components must handle null rather than
// substituting a stand-in image.

export const SERVICES = [
  {
    slug: "renovations",
    title: "Full Renovations",
    name: "Full & Partial Home Renovations",
    h1: "Home Renovations in Pickering & Durham Region",
    summary:
      "Whole-floor and single-room renovations, planned around how the space is actually used.",
    metaDescription:
      "Full and partial home renovations in Pickering, Ajax, Whitby and Durham Region. Layout changes, flooring, trim and paint coordinated as one finished result.",
    photo: "basementHallway",
    gallery: ["basementHallway", "flooringVinylPlank", "tileFeatureWall"],
    featured: true,
    intro:
      "We take on full and partial interior renovations across Durham Region — from a single room brought up to date, to a whole floor with the layout changed.",
    body: [
      "Every renovation starts with a walkthrough and a conversation about how you actually live in the space: where you come in with groceries, which room the family really sits in, what has annoyed you for years. The plan gets built around that rather than around a template.",
      "Because we self-perform most trades — framing, drywall, tile, flooring, paint — the finished result lines up. Trim heights match, floor transitions land where they should, and there's one person accountable for the whole thing rather than a chain of subcontractors pointing at each other.",
    ],
    pullQuote:
      "A renovation should make your home work better for you, not just look different.",
    scopeTitle: "What a renovation with us usually covers",
    scope: [
      "Layout changes, wall removal and space planning",
      "Flooring, trim and paint coordinated as one finished look",
      "Storage and built-ins sized to the room rather than off the shelf",
      "A written scope and timeline agreed before any work starts",
    ],
    faqs: [
      {
        question: "How long does a typical interior renovation take?",
        answer:
          "It depends entirely on scope. A single-room refresh is usually one to two weeks. A multi-room renovation that changes the layout runs from several weeks to a couple of months. You get a real timeline once we've scoped your project, not a number picked to sound good.",
      },
      {
        question: "Do I need a designer, or can you work from my own ideas?",
        answer:
          "Either works. Some clients arrive with a clear vision and reference photos; others want our input on layout and materials. We'll tell you honestly where a designer would earn their fee and where it isn't necessary.",
      },
      {
        question: "What's the most cost-effective way to update a space?",
        answer:
          "Paint, hardware and lighting go a long way before you touch flooring or walls. When we scope a project we flag the highest-impact, lowest-cost changes first — even when that means a smaller job for us.",
      },
    ],
  },

  {
    slug: "legal-basement",
    title: "Legal Basements",
    name: "Legal Basement Conversions",
    h1: "Legal Basement Conversions in Durham Region",
    summary:
      "Basements converted to legal, livable space — permits, egress, ceiling height and fire separation.",
    metaDescription:
      "Legal basement conversions in Pickering, Ajax and Whitby. Built to the permit, egress, ceiling height and fire separation requirements a second unit has to meet.",
    photo: "basementBedroom",
    gallery: ["basementBedroom", "basementHallway", "insulationFraming"],
    featured: true,
    intro:
      "We convert unfinished or non-compliant basements into legal, livable space — built to the permits, egress and fire-separation requirements that make a basement suite genuinely legal to occupy or rent.",
    body: [
      "\"Legal\" is not a marketing word here. It means egress windows sized correctly, ceiling height that meets the requirement, proper fire separation between units, and a build that has been through your municipality's permit and inspection process.",
      "That work starts before anything gets finished: moisture and grading assessed, insulation and vapour barrier detailed for a below-grade space, services roughed in to suit the final layout. Finishing work that looks done but wouldn't pass an inspection helps nobody.",
    ],
    pullQuote:
      "A legal basement isn't a finished basement with a nicer name — it's one that has been through permits and inspections.",
    scopeTitle: "What a legal conversion involves",
    scope: [
      "Permit drawings and code compliance — egress, ceiling height, fire separation",
      "Moisture assessment, insulation and vapour barrier suited to below grade",
      "Separate entrance, kitchen and bathroom where the unit requires them",
      "Coordination with your municipality's inspection schedule",
    ],
    faqs: [
      {
        question: "What makes a basement 'legal' rather than just finished?",
        answer:
          "Permits and inspections. A legal basement meets your municipality's requirements for egress windows, ceiling height and fire separation — and for a second unit, a separate entrance — and it has been signed off to prove it.",
      },
      {
        question: "Do I need a permit for a basement renovation?",
        answer:
          "For a legal second unit, or for any structural, electrical or plumbing change, yes. We handle the permit application and the inspections as part of the project rather than leaving you to navigate it.",
      },
      {
        question: "How do you deal with moisture in a basement?",
        answer:
          "We assess the space before finishing anything. Exterior grading, weeping tile condition and any existing dampness all change the insulation and vapour barrier approach — and occasionally the answer is to fix drainage before we finish anything at all.",
      },
    ],
  },

  {
    slug: "bath",
    title: "Bathrooms",
    name: "Bathroom Renovation",
    h1: "Bathroom Renovations in Pickering & Durham Region",
    summary:
      "Bathrooms rebuilt waterproofing-first, so the finish outlasts the day it was installed.",
    metaDescription:
      "Bathroom renovations in Pickering, Ajax, Whitby and Durham Region — waterproofing, ventilation and layout handled properly before any tile goes on the wall.",
    photo: "bathGlassDoor",
    gallery: ["bathGlassDoor", "bathVanityMirror", "bathMarbleDetail"],
    featured: true,
    intro:
      "From a fixture refresh to a full gut, we renovate bathrooms with waterproofing, ventilation and layout as the priorities — not just the finishes you can see.",
    body: [
      "A bathroom has to handle water every day for decades. What sits behind the tile and under the vanity decides whether it's still sound in fifteen years, so substrate prep and waterproofing get the same attention as the tile layout.",
      "Ventilation is the other thing people underestimate. An undersized fan is why grout goes black and paint peels above the shower, and it costs almost nothing to get right at rough-in stage.",
    ],
    pullQuote:
      "A bathroom has to work every day for years. That's what we build for first.",
    scopeTitle: "Decisions we work through with you",
    scope: [
      "Tub, walk-in shower, or both — sized honestly to the room you have",
      "Vanity and storage that fits what you actually keep in there",
      "Exhaust fan sized to the room, to control moisture long term",
      "Layered lighting — overall light plus task light at the vanity",
    ],
    faqs: [
      {
        question: "How long does a bathroom renovation take?",
        answer:
          "A fixture and finish refresh is usually one to two weeks. A full renovation involving plumbing moves or a layout change is typically three to five weeks.",
      },
      {
        question: "How do you prevent moisture problems afterwards?",
        answer:
          "Correct waterproofing behind the tile, an exhaust fan actually sized for the room, and moisture-resistant materials below the waterline. These get more of our attention than the visible finishes do.",
      },
      {
        question: "What works in a small bathroom?",
        answer:
          "A wall-mounted vanity or corner shower frees up floor space, and larger-format tile with fewer grout lines helps the room read bigger than it is.",
      },
    ],
  },

  {
    slug: "kitchen",
    title: "Kitchens",
    name: "Kitchen Renovation",
    h1: "Kitchen Renovations in Pickering & Durham Region",
    summary:
      "Kitchens planned around how you cook and gather, then built — cabinetry, counters, and the services behind the wall.",
    metaDescription:
      "Kitchen renovations in Pickering, Ajax, Whitby and Durham Region — from cabinet refacing to full gut renovations, with layout and workflow settled before finishes.",
    // No photograph of a completed Five Lights Reno kitchen exists yet. The
    // previous site used a stock image here; it has been deleted rather than
    // presented as our work. See the KITCHEN PHOTO GAP note in data/media.js.
    photo: null,
    gallery: [],
    featured: false,
    intro:
      "From cabinet refacing to a full gut renovation, we plan kitchens around how you actually cook and gather in them, not around what photographs well.",
    body: [
      "Layout and workflow come first: counter space beside the stove rather than across the room, storage where you reach for it, and sightlines to wherever everyone else ends up standing. Finishes come after that conversation, not before it.",
      "Then there's the part nobody sees — the electrical and plumbing changes a new layout or a different appliance actually needs. Getting that scoped early is what keeps a kitchen project from stalling halfway through.",
    ],
    pullQuote:
      "A kitchen renovation should fix how the room works, and look good doing it.",
    scopeTitle: "Key decisions we work through with you",
    scope: [
      "Layout and workflow — L-shape, U-shape, galley or open-concept",
      "Storage sized to what you own, not to a standard cabinet run",
      "Countertops weighed on durability and upkeep against budget",
      "Appliance, electrical and plumbing changes needed to support the plan",
    ],
    faqs: [
      {
        question: "How long does a kitchen renovation take?",
        answer:
          "Cabinet refacing and cosmetic updates can be done in about a week. A full gut with layout changes is typically four to eight weeks, and custom cabinetry lead times are usually what set the pace.",
      },
      {
        question: "Can I keep my kitchen usable during the work?",
        answer:
          "Partially, depending on scope. We'll tell you upfront what stays functional and for how long, and help set up a temporary cooking space if there's a stretch where you'll need one.",
      },
      {
        question: "What's a budget-friendly way to update a kitchen?",
        answer:
          "Refinishing or repainting existing cabinets instead of replacing them, plus new hardware and a laminate or butcher block counter, gets you most of the visual change for a fraction of a full renovation.",
      },
    ],
  },

  {
    slug: "tile",
    title: "Tile",
    name: "Tile Installation",
    h1: "Tile Installation in Pickering & Durham Region",
    summary:
      "Floor and wall tile set level and square — large-format porcelain through to mosaic detail.",
    metaDescription:
      "Tile installation across Durham Region: floors, backsplashes, shower surrounds and feature walls, with substrate prep and waterproofing handled before the tile goes on.",
    photo: "tileFeatureWall",
    gallery: ["tileFeatureWall", "tileFloorLargeFormat", "tileEntryway", "tileLevelingClips"],
    featured: false,
    intro:
      "Floors, backsplashes, shower surrounds and feature walls — ceramic, porcelain, natural stone and glass, set with as much attention to what's behind them as to the tile itself.",
    body: [
      "Layout gets decided before the first tile is buttered: where the cuts land, how the pattern meets a doorway, which wall the eye goes to first. Large-format tile in particular is unforgiving — a subfloor that isn't level shows up as lippage on every joint.",
      "In wet areas, waterproofing is the job. A good-looking shower that leaks behind the wall is a problem waiting to surface, so the substrate work gets the same care as the finish.",
    ],
    pullQuote:
      "Tile is only as good as what's behind it. Layout and waterproofing come before grout.",
    scopeTitle: "Where tile usually goes in",
    scope: [
      "Floors — durable and water-resistant, for kitchens, baths and hallways",
      "Walls — backsplashes and feature walls",
      "Showers and surrounds, fully waterproofed for wet areas",
      "Entryways and thresholds, where transitions have to be right",
    ],
    faqs: [
      {
        question: "What tile material should I choose?",
        answer:
          "Porcelain handles moisture and traffic best for floors and showers. Natural stone like marble or granite looks premium but needs sealing and ongoing upkeep. We'll match the material to the room rather than to a trend.",
      },
      {
        question: "How do I choose tile size for a small space?",
        answer:
          "Larger format with fewer grout lines tends to make a small bathroom or kitchen feel bigger. Smaller tile suits detailed work like backsplashes where the pattern is the point.",
      },
      {
        question: "How do I maintain tile long term?",
        answer:
          "Regular sweeping and mopping, and periodic re-sealing for natural stone. Grout needs the most attention — we'll tell you what to expect for the specific tile you choose.",
      },
    ],
  },

  {
    slug: "flooring",
    title: "Flooring",
    name: "Flooring Installation",
    h1: "Flooring Installation in Pickering & Durham Region",
    summary:
      "Vinyl plank, laminate, hardwood and tile, laid over a subfloor that's been properly prepped.",
    metaDescription:
      "Flooring installation in Pickering, Ajax, Whitby and Durham Region — hardwood, engineered wood, laminate, luxury vinyl plank and tile, including subfloor preparation.",
    photo: "flooringVinylPlank",
    gallery: ["flooringVinylPlank", "basementHallway", "tileEntryway"],
    featured: false,
    intro:
      "Hardwood, engineered wood, laminate, luxury vinyl plank and tile — we help you pick a floor that suits the room and how it gets used, then install it properly, subfloor included.",
    body: [
      "The right floor depends on the room more than on any current trend. A mudroom and a bedroom have completely different demands, and we'll say so even when it means recommending something other than what you first asked about.",
      "Subfloor prep is the part that gets skipped when a price looks too good. Levelling, moisture checks and squeak fixes happen before anything goes down, because none of them can be fixed afterwards.",
    ],
    pullQuote:
      "The best flooring choice is the one that still looks right in five years, not just on installation day.",
    scopeTitle: "What we weigh up with you",
    scope: [
      "High-traffic areas — tile, laminate or engineered hardwood",
      "Moisture-prone rooms and below grade — vinyl plank or ceramic tile",
      "Comfort underfoot, where warmth matters more than hardness",
      "Subfloor condition, levelling and moisture, checked before ordering",
    ],
    faqs: [
      {
        question: "What flooring holds up best with kids or pets?",
        answer:
          "Luxury vinyl plank and engineered hardwood with a scratch-resistant finish are usually the best balance of durability and looks for a busy household.",
      },
      {
        question: "Does flooring always need professional installation?",
        answer:
          "Laminate and click-vinyl are reasonably DIY-friendly. Hardwood, large-format tile, and anything needing subfloor repair is where a bad install shows quickly — that's where we'd push back on doing it yourself.",
      },
      {
        question: "How do I look after it once it's in?",
        answer:
          "Regular sweeping and the right cleaning product for the material matter more than anything else. We give you specific care instructions for whatever we install.",
      },
    ],
  },

  {
    slug: "drywall-taping",
    title: "Drywall & Taping",
    name: "Drywall & Taping",
    h1: "Drywall & Taping in Pickering & Durham Region",
    summary:
      "Framing, insulation, board and taping — finished flat enough to take paint cleanly.",
    metaDescription:
      "Drywall installation, repair and taping across Durham Region. New board, patches after electrical or plumbing work, and skim coating to a smooth paint-ready finish.",
    photo: "insulationFraming",
    gallery: ["insulationFraming", "basementHallway"],
    featured: false,
    intro:
      "Every finish — paint, wallpaper, tile — depends on the drywall underneath being flat, square and properly taped. We handle new installs, repairs, and taping to a paint-ready finish.",
    body: [
      "That includes the unglamorous work: patching after electrical or plumbing has been run, cutting out cracked or water-damaged sections, and skim-coating walls that need an even surface before paint.",
      "The last step is the one that separates a good job from an average one — checking the whole wall under raking light before calling it done, because that's exactly how the seams will show once the room is lit.",
    ],
    pullQuote: "Drywall work is invisible when it's done right. That's the point.",
    scopeTitle: "How the work goes",
    scope: [
      "Hang and screw — panels secured to studs on a proper layout",
      "Tape — joints and fastener holes covered with mesh or paper tape",
      "Mud and sand — compound applied in coats and sanded back",
      "Final check under raking light before we hand it to the painter",
    ],
    faqs: [
      {
        question: "Can drywall be repaired without redoing the whole wall?",
        answer:
          "Usually, yes. Holes, cracks and water-damaged sections can be cut out, patched and blended into the surrounding wall so the repair disappears once it's painted.",
      },
      {
        question: "Why hire this out instead of doing it myself?",
        answer:
          "Taping and mudding is a finish trade. Uneven sanding or a proud seam shows up under certain light no matter how good the paint is — it's one of the areas where experience is hardest to fake.",
      },
      {
        question: "What should a drywall quote include?",
        answer:
          "A clear breakdown of area, materials, and whether ceiling texture or skim coating is included — so nothing appears as an add-on once work has started.",
      },
    ],
  },

  {
    slug: "painting",
    title: "Painting",
    name: "Painting & Refinishing",
    h1: "Interior & Exterior Painting in Pickering & Durham Region",
    summary:
      "Interior painting and wood refinishing, with the prep and masking done first.",
    metaDescription:
      "Interior and exterior painting in Pickering, Ajax, Whitby and Durham Region — patching, sanding and priming done properly so the finish lasts beyond day one.",
    photo: "staircaseAfter",
    gallery: ["staircaseAfter", "staircaseInProgress", "staircaseMasking", "staircaseBefore"],
    featured: false,
    intro:
      "Interior and exterior painting with real surface prep — patching, sanding, priming — so the finish still looks right in a year rather than only on the day we leave.",
    body: [
      "Colour is the fun part, but prep is what decides whether paint looks good in six months or shows brush marks and peels at the edges. We don't shorten it to make a schedule.",
      "It's the same discipline on refinishing work: masking and dust containment before a sander comes out, so the rest of your house isn't paying for the room we're working in.",
    ],
    pullQuote: "Paint only looks as good as the surface prep underneath it.",
    scopeTitle: "What shapes the recommendation",
    scope: [
      "Room function — calmer tones for bedrooms, more contrast for work spaces",
      "Natural light, which changes how a colour actually reads on the wall",
      "Finish — matte hides imperfections, satin cleans up, gloss suits wet rooms",
      "Prep — patching, sanding and priming before any topcoat",
    ],
    faqs: [
      {
        question: "How do I choose the right paint finish?",
        answer:
          "Flat and matte hide wall imperfections but mark easily. Satin and eggshell are more washable and suit living spaces. Gloss is durable and wipes clean, which works in kitchens and bathrooms, but it shows every flaw underneath.",
      },
      {
        question: "Is it worth hiring a painter?",
        answer:
          "For a single accent wall, DIY is perfectly reasonable. For whole-room or exterior work, prep and even coverage are where a professional job looks visibly different — especially in strong daylight.",
      },
      {
        question: "How do you keep dust out of the rest of the house?",
        answer:
          "Sheeting and taped-off openings before sanding starts, and containment kept up for the length of the job. It's most of what makes living through a renovation bearable.",
      },
    ],
  },

  {
    slug: "exterior-reno",
    title: "Exterior",
    name: "Exterior Renovation",
    h1: "Exterior Renovations in Pickering & Durham Region",
    summary:
      "Doors, garage doors, trim and siding updates that change a home's front elevation.",
    metaDescription:
      "Exterior renovations across Durham Region — doors, garage doors, trim, siding and exterior painting chosen to hold up to Southern Ontario winters.",
    photo: "exteriorBrickGarage",
    gallery: ["exteriorBrickGarage"],
    featured: false,
    intro:
      "Siding, doors, garage doors, trim and exterior painting — planned to hold up to a Southern Ontario winter, not just to the season the work started in.",
    body: [
      "Material choice matters more outside than in. Durability, weather resistance and how much maintenance you're willing to do all factor into what we'd recommend for your particular elevation.",
      "The highest-return exterior work is usually smaller than people expect: the front entry, the garage doors and the trim carry most of a home's curb appeal, and refinishing them changes the look of the house for a contained cost.",
    ],
    pullQuote:
      "Your home's exterior has to survive winters here, not just look good in listing photos.",
    scopeTitle: "Common exterior projects",
    scope: [
      "Front door, garage doors and entry refresh",
      "Trim, gable and bay window surrounds",
      "Siding — vinyl, fibre cement or engineered wood",
      "Exterior painting and refinishing",
    ],
    faqs: [
      {
        question: "What should I consider when choosing exterior materials?",
        answer:
          "Durability through our winters, ongoing maintenance, and upfront cost. We'll walk the trade-offs for your specific home rather than defaulting to one material for everyone.",
      },
      {
        question: "Do I need a permit for exterior work?",
        answer:
          "Often, depending on scope and your municipality. We check before starting and handle the application where one is required.",
      },
      {
        question: "What's the highest-impact exterior upgrade?",
        answer:
          "A refreshed front entry — door, lighting, walkway — plus clean paint or refinished garage doors usually changes how a home reads more than any single larger project.",
      },
    ],
  },
];

// ── Portfolio CMS category labels ───────────────────────────────────────────
// The `portfolio_projects` table stores a free-text `category` per project,
// and the admin dashboard's dropdown is generated from this list. The values
// below are the ones already written to the database by BACKEND/seed_portfolio.sql
// — so they are FROZEN, exactly like the slugs.
//
// They're kept separate from the display names on purpose: `slug: "bath"` now
// presents as "Bathroom Renovation" in the UI, but its stored category is
// still "Bath". Renaming these would orphan every existing row's category and
// break the mapping from a CMS project back to its service page.
export const SERVICE_CATEGORY_BY_SLUG = {
  renovations: "Renovations",
  "legal-basement": "Legal Basement",
  bath: "Bath",
  kitchen: "Kitchen",
  tile: "Tile",
  flooring: "Flooring",
  "drywall-taping": "Drywall & Taping",
  painting: "Painting",
  "exterior-reno": "Exterior Reno",
};

/** Options for the admin dashboard's category dropdown. */
export const CATEGORY_OPTIONS = SERVICES.map(
  (service) => SERVICE_CATEGORY_BY_SLUG[service.slug]
);

/** Maps a stored CMS category back to its service, for cross-linking. */
export const getServiceByCategory = (category) =>
  SERVICES.find(
    (service) => SERVICE_CATEGORY_BY_SLUG[service.slug] === category
  );

export const getServiceBySlug = (slug) =>
  SERVICES.find((service) => service.slug === slug);

// The three services given the large featured treatment on the services page
// and homepage index; the rest render as a secondary list.
export const FEATURED_SERVICES = SERVICES.filter((service) => service.featured);
export const SECONDARY_SERVICES = SERVICES.filter((service) => !service.featured);
